"use client"

import { useMemo } from 'react'
import { cn } from '@/shared/utils/tw'
import type { KioskIndicatorProps } from '../types/kiosk-config'
import { INDICATOR_STATUS_TEXT } from '../types/kiosk-config'

// 사이즈 프리셋
const SIZE_PRESETS = {
  kiosk: {
    containerHeight: '9.58cqw',
    innerGap: '1cqw',
    dropShadow: 'drop-shadow(0 0 0.93cqw rgba(0, 0, 0, 0.25))',
    fontSize: '4.44cqw',
    iconSize: '5.37cqw',
    iconPadding: '0',
    listeningGap: '0.4cqw',
    barWidth: '0.45cqw',
    loadingGap: '0.45cqw',
    dotSize: '0.7cqw',
    touchSize: '4.44cqw',
  },
  mobile: {
    containerHeight: '9.58cqw',
    innerGap: '1cqw',
    dropShadow: 'drop-shadow(0 0 8px rgba(0, 0, 0, 0.25))',
    fontSize: '18px',
    iconSize: '32px',
    iconPadding: '3px',
    listeningGap: '2px',
    barWidth: '3px',
    loadingGap: '3px',
    dotSize: '5px',
    touchSize: '32px',
  },
} as const

type IndicatorSize = keyof typeof SIZE_PRESETS

/**
 * 키오스크 상태 인디케이터
 *
 * Figma 디자인:
 * - Listening (node-id=2011-368): 음파 아이콘 + "듣고 있어요"
 * - Loading (node-id=2010-206): 로딩 스피너 + "생각 중이에요"
 * - Speaking (node-id=2010-231): 아이콘 없음 + "화면을 터치하면 멈출 수 있어요"
 *
 * 스타일:
 * - Status Button: blur(94px), border-radius 999px
 *
 * Note: Bubble(말풍선)은 KioskToastContainer에서 토스트로 처리
 */
export const KioskIndicator = ({
  state,
  statusText,
  onScreenTouch,
  pitch = 0,
  selectedLanguage,
  positionClass,
  size = 'kiosk',
}: Omit<KioskIndicatorProps, 'bubbleText'> & { positionClass?: string; size?: IndicatorSize }) => {
  if (state === 'idle') return null

  const displayStatusText = statusText || INDICATOR_STATUS_TEXT[selectedLanguage][state]
  const s = SIZE_PRESETS[size]

  return (
    <div className="absolute inset-0 z-50 pointer-events-none">
      {/* Status Button - 화면 최하단 */}
      <div
        className={cn(
          'absolute left-1/2 -translate-x-1/2',
          positionClass || 'bottom-[16%]',
          'rounded-full',
          'drop-shadow-[0_0_0.93cqw_rgba(0,0,0,0.2)]',
          'px-[4.63%] py-[1.72%]',
          'flex items-center justify-center',
          state === 'speaking' && 'pointer-events-auto cursor-pointer'
        )}
        style={{ height: s.containerHeight }}
        onClick={state === 'speaking' ? onScreenTouch : undefined}
      >
        <div className="flex items-center justify-center" style={{ gap: s.innerGap, filter: s.dropShadow }}>
          {/* 아이콘 - 각 상태별 아이콘 렌더링 */}
          {state === 'listening' && <ListeningIcon pitch={pitch} size={size} />}
          {state === 'loading' && <LoadingIcon size={size} />}
          {state === 'speaking' && <TouchIcon size={size} />}

          {/* 상태 텍스트 */}
          <span
            className={cn(
              'font-pretendard font-medium text-white',
              'leading-[1.58]',
              'whitespace-nowrap'
            )}
            style={{ fontSize: s.fontSize }}
          >
            {displayStatusText}
          </span>
        </div>
      </div>
    </div>
  )
}

/**
 * Listening 아이콘 (5개 막대 이퀄라이저)
 *
 * Figma: 크기 116x116, 막대 너비 9.67px
 * 막대 높이: 29px, 19.33px, 96.67px, 58px, 77.33px
 *
 * pitch 값에 따라 막대 높이가 동적으로 변화
 * - 대기 상태 (pitch=0): 모든 막대가 낮고 균일한 높이 (15%)
 * - 활성 상태 (pitch>0): Figma 비율대로 막대 높이 변화
 */
const ListeningIcon = ({ pitch = 0, size = 'kiosk' as IndicatorSize }: { pitch: number; size?: IndicatorSize }) => {
  const s = SIZE_PRESETS[size]
  const barRatios = [0.30, 0.20, 1.0, 0.60, 0.80]

  const bars = useMemo(() => {
    const normalizedPitch = Math.min(pitch / 100, 1)

    return barRatios.map((baseRatio, index) => {
      const idleHeight = 15
      const activeHeight = baseRatio * 80 * normalizedPitch
      const height = Math.min(100, idleHeight + activeHeight)
      return height
    })
  }, [pitch])

  return (
    <div
      className="flex items-center justify-center"
      style={{ width: s.iconSize, height: s.iconSize, padding: s.iconPadding, gap: s.listeningGap }}
    >
      {bars.map((height, index) => (
        <div
          key={index}
          className={cn(
            'bg-white rounded-full',
            'transition-all duration-75 ease-out'
          )}
          style={{
            width: s.barWidth,
            height: `${height}%`,
          }}
        />
      ))}
    </div>
  )
}

/**
 * Loading 아이콘 (4개 도트 웨이브 애니메이션)
 *
 * Figma: 207x207 영역, 도트 19.4px, ~22px 상승
 * 왼쪽부터 1→2→3→4 순서로 위로 올라갔다가 내려오는 웨이브 효과
 */
const LoadingIcon = ({ size = 'kiosk' as IndicatorSize }: { size?: IndicatorSize }) => {
  const s = SIZE_PRESETS[size]
  return (
    <div
      className={cn(
        'flex items-center justify-center',
      )}
      style={{ width: s.iconSize, height: s.iconSize, padding: s.iconPadding, gap: s.loadingGap }}
    >
      {[0, 1, 2, 3].map((index) => (
        <div
          key={index}
          className="bg-white rounded-full"
          style={{
            width: s.dotSize,
            height: s.dotSize,
            animation: 'kiosk-wave 1.6s ease-in-out infinite',
            animationDelay: `${index * 0.25}s`,
          }}
        />
      ))}
      <style jsx>{`
        @keyframes kiosk-wave {
          0%, 100% { transform: translateY(0); }
          30% { transform: translateY(-0.65cqw); }
          60% { transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}

/**
 * Speaking 터치 아이콘 (carbon:touch-1 기반)
 *
 * Figma: 160x160 영역
 * 손가락 터치 아이콘 (인라인 SVG)
 */
const TouchIcon = ({ size = 'kiosk' as IndicatorSize }: { size?: IndicatorSize }) => {
  const s = SIZE_PRESETS[size]
  return (
    <div
      className={cn(
        'flex items-center justify-center',
      )}
      style={{
        width: s.touchSize,
        height: s.touchSize,
        animation: 'kiosk-touch-poke 2s ease-in-out infinite',
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="100" height="120" viewBox="0 0 100 120" fill="none" className="w-full h-full">
        <path d="M67.7248 24.0063C68.0392 26.1923 66.2195 27.9917 64.011 27.9917C61.8026 27.9917 60.0525 26.1838 59.6137 24.0194C58.8387 20.1966 56.9553 16.6529 54.1561 13.8538C50.4065 10.1041 45.3209 7.99763 40.0181 7.99763C34.7154 7.99763 29.6298 10.1041 25.8802 13.8538C23.081 16.6529 21.1976 20.1966 20.4226 24.0194C19.9838 26.1838 18.2337 27.9917 16.0253 27.9917C13.8168 27.9917 11.9971 26.1923 12.3115 24.0063C13.1674 18.0558 15.9241 12.4995 20.225 8.19858C25.4745 2.94912 32.5943 0 40.0181 0C47.442 0 54.5618 2.94912 59.8113 8.19858C64.1122 12.4995 66.8689 18.0558 67.7248 24.0063Z" fill="white" />
        <path d="M88.0036 55.9834C85.9376 55.9908 83.9101 56.5425 82.1253 57.5829C81.0472 55.873 79.5545 54.4632 77.7858 53.4845C76.0171 52.5057 74.0297 51.9898 72.0083 51.9846C69.9424 51.992 67.9149 52.5437 66.13 53.5841C64.6752 51.2949 62.4875 49.5663 59.9239 48.6803C57.3603 47.7943 54.5721 47.8032 52.0142 48.7056V27.9917C52.0142 24.8101 50.7503 21.7587 48.5005 19.5089C46.2508 17.2592 43.1994 15.9953 40.0178 15.9953C36.8361 15.9953 33.7848 17.2592 31.535 19.5089C29.2852 21.7587 28.0213 24.8101 28.0213 27.9917V72.3786L19.104 66.3004C17.0597 64.778 14.5748 63.9637 12.0261 63.981C9.64668 63.9752 7.31929 64.677 5.33981 65.9973C3.36033 67.3176 1.81818 69.1967 0.909428 71.3957C0.000674429 73.5947 -0.233628 76.0143 0.23631 78.3468C0.706248 80.6793 1.8592 82.8194 3.54857 84.495L35.5391 113.686C40.0681 117.74 45.936 119.976 52.0142 119.964H72.0083C79.4322 119.964 86.552 117.015 91.8014 111.766C97.0509 106.516 100 99.3966 100 91.9728V67.9799C100 64.7982 98.7361 61.7469 96.4863 59.4971C94.2366 57.2473 91.1852 55.9834 88.0036 55.9834ZM92.0024 91.9728C92.0024 97.2755 89.8959 102.361 86.1462 106.111C82.3966 109.86 77.3111 111.967 72.0083 111.967H52.0142C47.9722 112.016 44.0501 110.595 40.9775 107.968L9.1869 78.7767C8.44814 78.0318 8.03166 77.0265 8.02724 75.9775C8.02724 75.2349 8.23404 74.5069 8.62446 73.8752C9.01489 73.2435 9.57351 72.733 10.2377 72.4008C10.902 72.0687 11.6455 71.9281 12.3852 71.9948C13.1248 72.0615 13.8312 72.3329 14.4253 72.7784L23.5008 78.9968C28.8083 82.6334 36.019 78.8331 36.019 72.3993V27.9917C36.019 26.9312 36.4403 25.914 37.1902 25.1641C37.9401 24.4142 38.9572 23.9929 40.0178 23.9929C41.0783 23.9929 42.0954 24.4142 42.8454 25.1641C43.5953 25.914 44.0166 26.9312 44.0166 27.9917V67.9799C44.0166 70.1883 45.8069 71.9787 48.0154 71.9787C50.2239 71.9787 52.0142 70.1883 52.0142 67.9799V59.9822C52.0142 58.9217 52.4355 57.9046 53.1854 57.1546C53.9354 56.4047 54.9525 55.9834 56.013 55.9834C57.0736 55.9834 58.0907 56.4047 58.8406 57.1546C59.5905 57.9046 60.0118 58.9217 60.0118 59.9822V67.9799C60.0118 70.1883 61.8022 71.9787 64.0107 71.9787C66.2191 71.9787 68.0095 70.1883 68.0095 67.9799V63.981C68.0095 62.9205 68.4308 61.9034 69.1807 61.1535C69.9306 60.4035 70.9477 59.9822 72.0083 59.9822C73.0688 59.9822 74.086 60.4035 74.8359 61.1535C75.5858 61.9034 76.0071 62.9205 76.0071 63.981V67.9799C76.0071 70.1883 77.7974 71.9787 80.0059 71.9787C82.2144 71.9787 84.0047 70.1883 84.0047 67.9799C84.0047 66.9193 84.426 65.9022 85.176 65.1523C85.9259 64.4023 86.943 63.981 88.0036 63.981C89.0641 63.981 90.0812 64.4023 90.8311 65.1523C91.5811 65.9022 92.0024 66.9193 92.0024 67.9799V91.9728Z" fill="white" />
      </svg>
      <style jsx>{`
        @keyframes kiosk-touch-poke {
          0%, 100% { transform: translateY(0); }
          15% { transform: translateY(10%); }
          30% { transform: translateY(0); }
          45% { transform: translateY(10%); }
          60% { transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}

export default KioskIndicator
